import { publicHref } from '../lib/publicUrls';
import { Link } from 'react-router-dom';
import ProgramJourney from '../components/ProgramJourney';
import ClassSchedule from '../components/ClassSchedule';
import SEO from '../components/SEO';
import { MEDIA_SLOTS } from '../lib/mediaTruth';
import { ServiceHero } from '../components/ServiceHeader';
import './ReformerPilatesPage.css';

const benefits = [
  {
    title: 'Full-Body Toning',
    text: 'Reformer Pilates engages multiple muscle groups simultaneously, promoting comprehensive toning and sculpting for a leaner physique.',
  },
  {
    title: 'Core Strength Development',
    text: 'The focus on core engagement in reformer Pilates builds a strong and stable core, supporting better posture, balance, and overall spinal health.',
  },
  {
    title: 'Improved Flexibility',
    text: 'The dynamic and controlled movements on the reformer enhance flexibility, fostering an increased range of motion and reduced muscle stiffness.',
  },
  {
    title: 'Low-Impact, Joint Friendly',
    text: 'With its gentle, low-impact nature, reformer Pilates provides an effective workout without placing excessive stress on joints, making it suitable for all fitness levels and ages.',
  },
];

const programFacts = [
  {
    label: 'Location',
    value: 'Brentwood',
    detail: 'Delta Valley Health Club · 120 Guthrie Ln.',
  },
  {
    label: 'First step',
    value: 'Free 30-minute demo',
    detail: 'Request a demo and the team will confirm current availability and program details.',
  },
  {
    label: 'Class times',
    value: 'Updated live',
    detail: 'Use the Reformer Pilates schedule below for current times, locations, and instructors.',
  },
];

const ReformerPilatesPage = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Reformer Pilates at Delta Valley Health Club',
    description: 'Low-impact reformer Pilates for full-body strength, core development, and flexibility in Brentwood, California.',
    provider: {
      '@type': 'HealthClub',
      name: 'Delta Valley Health Club',
      telephone: '(925) 240-2990',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '120 Guthrie Ln',
        addressLocality: 'Brentwood',
        addressRegion: 'CA',
        postalCode: '94513',
      },
    },
  };

  return (
    <main className="dv-reformer" data-testid="reformer-pilates-page">
      <SEO
        title="Reformer Pilates in Brentwood | Delta Valley Health Club"
        description="Discover low-impact reformer Pilates at Delta Valley Health Club in Brentwood. Build strength, improve flexibility, and request a free 30-minute demo."
        keywords="reformer Pilates Brentwood, Pilates classes Brentwood CA, low impact workout, free Pilates demo"
        schema={schema}
      />

      <ServiceHero
        id="reformer-title"
        media={MEDIA_SLOTS.programsReformerPilates}
        disclosure="P01 program image: owner-supplied Sept 30 photo"
        current="Reformer Pilates"
        label="Sculpt, tone, and strengthen."
        title={<><span>Sculpt, tone,</span><em>and strengthen.</em></>}
        lede="A supportive, adaptable workout for members who want strength, mobility, and precise movement."
        actions={<>
          <a className="dv-button dv-button--light" href="#reformer-demo">Request a Free 30-Minute Demo</a>
          <a className="dv-button dv-button--outline-light" href="#reformer-schedule">View the Schedule</a>
        </>}
        facts={["Top Reformer Pilates Studio in Brentwood", "Low-impact, joint friendly", "Free 30-minute demo"]}
      />

      <section className="dv-reformer__introduction" aria-labelledby="reformer-introduction-title">
        <div className="dv-reformer__section-heading">
          <p className="dv-eyebrow dv-eyebrow--dark">Why Reformer Pilates?</p>
          <h2 className="dv-size-heading" id="reformer-introduction-title">
            <span>Low-impact movement.</span>
            <em>Full-body focus.</em>
          </h2>
        </div>
        <div className="dv-reformer__introduction-copy">
          <p className="dv-size-body">
            Discover the gentle power of low-impact reformer Pilates—sculpt,
            tone, and strengthen with grace. Elevate your fitness journey,
            prioritize joint health, and experience a full-body transformation.
            Join us for a unique exercise blend that nurtures and challenges,
            delivering results that resonate with your body and well-being.
          </p>
        </div>
      </section>

      <section className="dv-reformer__benefits" aria-labelledby="reformer-benefits-title">
        <div className="dv-reformer__benefits-heading">
          <p className="dv-eyebrow">Movement outcomes</p>
          <h2 className="dv-size-heading" id="reformer-benefits-title">Four ways the reformer supports your practice.</h2>
        </div>
        <ol className="dv-reformer__benefit-list">
          {benefits.map((benefit, index) => (
            <li className="dv-size-body" key={benefit.title}>
              <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h3 className="dv-size-subheading">{benefit.title}</h3>
                <p className="dv-size-body">{benefit.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="dv-reformer__facts" aria-labelledby="reformer-facts-title">
        <div className="dv-reformer__facts-heading">
          <p className="dv-eyebrow">Program facts</p>
          <h2 className="dv-size-heading" id="reformer-facts-title">
            <span>Know the setting.</span>
            <em>Choose your next step.</em>
          </h2>
        </div>
        <ol className="dv-reformer__fact-list">
          {programFacts.map((fact, index) => (
            <li className="dv-size-body" key={fact.label}>
              <span className="dv-reformer__fact-number" aria-hidden="true">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div>
                <p className="dv-reformer__fact-label">{fact.label}</p>
                <h3 className="dv-size-subheading">{fact.value}</h3>
                <p className="dv-size-body">{fact.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section id="reformer-schedule" className="dv-reformer__schedule" aria-labelledby="reformer-schedule-title">
        <div className="dv-reformer__schedule-introduction">
          <p className="dv-eyebrow dv-eyebrow--dark">Updated live</p>
          <h2 className="dv-size-heading" id="reformer-schedule-title">Reformer Pilates Schedule</h2>
          <p className="dv-size-body">
            View current Reformer Pilates class times, locations, and instructors.
            The schedule opens on Reformer Pilates; tap a class for details or to reserve a spot.
          </p>
        </div>
        <div className="dv-reformer__schedule-frame">
          <ClassSchedule
            focus="reformer"
            categoryId="10"
            title="Delta Valley Health Club Reformer Pilates weekly schedule"
          />
        </div>
      </section>

      <ProgramJourney
        id="reformer-demo" tone="purple" testId="reformer-demo-journey" photo="P01"
        source="reformer-pilates-demo" location="Brentwood"
        eyebrow="Free 30-Minute Demo"
        title={['Transform yourself at Brentwood’s Best Pilates Studio.', 'Request yours in about a minute.']}
        lede="Tell us you’re interested in the complimentary demo, and the team will follow up with availability and current program details."
        note={<>Questions first? <a href="tel:9252402990">Call (925) 240-2990</a>, <Link to={publicHref("/programs")}>see all programs</Link> or <Link to={publicHref("/book-a-tour")}>book a tour</Link>.</>}
        planTitle="Your demo request" request={['Request', 'Free 30-minute Reformer demo']}
        reviewNoun="demo request" startLabel="Start My Demo Request" buttonText="Request My Demo"
        successMessage="Our Brentwood team will contact you with available demo times and next steps."
      />
    </main>
  );
};

export default ReformerPilatesPage;
